import React from 'react';
import styled from 'styled-components';
import UniversalIconBtn from '../../../shared/component/headless/button/UniversalIconBtn';
import BlockWithVisible from '../BlockWithVisible';
import { useTextViewStore } from '../../../shared/store/TextViewStore';
import { useEditMode } from '../../../shared/hooks/useEditMode';
import { SortDirection, TextSortOrder } from '../../../shared/enum/EnumRepository';
import { useTextListStore } from '../../../shared/store/TextListStore';
import BlockWithSortVisible from '../BlockWithSortVisible';
import deleteIcon from '../../../asset/icon/delete.png';
import { useUnsavedChanges } from '../../../shared/hooks/useUnsavedChanges';
import TextFontSizeToggle from '../TextFontSizeToggle';
import TextOverviewEditActions from '../TextOverviewEditActions';

const TextColumnInfoMobile: React.FC = () => {
  const activeText = useTextViewStore((s) => s.activeText);
  const setActiveText = useTextViewStore((s) => s.setActiveText);
  const { isEditMode } = useEditMode();
  const { requestExitEditMode } = useUnsavedChanges();
  const sortData = useTextListStore((s) => s.sortData);
  const setSortData = useTextListStore((s) => s.setSortData);

  const handleDirectionChange = (field: TextSortOrder) => {
    if (isEditMode) {
      requestExitEditMode({ confirmWhenClean: true });
      return;
    }

    // 현재 정렬 필드와 같으면 방향만 변경, 다르면 해당 필드로 정렬하고 ASC로 설정
    if (sortData.sort === field) {
      setSortData({
        ...sortData,
        direction: sortData.direction === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC
      });
    } else {
      setSortData({ sort: field, direction: SortDirection.ASC });
    }
  };

  const handleDelete = () => {
    setActiveText(null)
  }

  return (
    <ColumnInfoComp>
      <InfoContainer $isActive={!!activeText}>
        <TitleBlock onClick={() => handleDirectionChange(TextSortOrder.TITLE)}>
          Title
          <SelectDirection
            $isASC={sortData.sort === TextSortOrder.TITLE && sortData.direction === SortDirection.ASC}
            $isSortField={sortData.sort === TextSortOrder.TITLE}
          >
            ▼
          </SelectDirection>
        </TitleBlock>
        <BlockWithVisible
          width={"2"}
          isColumn={true}
          value={"Author"}
        />
        <BlockWithSortVisible
          width={"1"}
          isColumn={true}
          field={TextSortOrder.YEAR}
          value={"Year"}
        />
      </InfoContainer>
      {activeText &&
        <OverviewBlock>
          <OverviewText>Overview</OverviewText>
          {!isEditMode && (
            <OverviewActions>
              <TextFontSizeToggle />
              <UniversalIconBtn
                size="small"
                border={false}
                isTransparent={true}
                backgroundImg={deleteIcon}
                onClick={handleDelete}
                aria-label="Close text viewer"
              />
            </OverviewActions>
          )}
          <TextOverviewEditActions />
        </OverviewBlock>
      }
    </ColumnInfoComp>
  )
}

const ColumnInfoComp = styled.article`
  width: 100%;
  height: 36px;
  display: flex;
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_02};

  ${({ theme }) => theme.typography.Body_03_1};
`;

const InfoContainer = styled.article<{ $isActive: boolean }>`
  width: ${({ $isActive }) => $isActive ? '0%' : '100%'};
  opacity: ${({ $isActive }) => $isActive ? 0 : 1};
  overflow-x: hidden;
  height: 32px;
  display: flex;
  transition: all 0.7s ease-in-out;
`;

const TitleBlock = styled.div`
  flex: 3;

  display: flex;
  align-items: center;
  padding-left: 4px;
  cursor: pointer;
  user-select: none;
`

const SelectDirection = styled.span<{ $isSortField: boolean, $isASC: boolean }>`
  margin-left: 4px;
  ${({ theme }) => theme.typography.Footer_02};
  color: ${({ theme, $isSortField }) => $isSortField ? theme.colors.color_Gray_03 : theme.colors.color_Gray_05};
  transform: ${({ $isASC, $isSortField }) => {
    if (!$isSortField) return "scale(0.7)"; // 기본: dim 처리된 ▼
    return $isASC ? "scale(0.7)" : "scaleY(-1) scale(0.7)"; // ASC면 그대로, DESC면 뒤집기
  }};
  transition: transform 0.6s ease-in-out;
`;

const OverviewBlock = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const OverviewText = styled.div`
  display: flex;
  user-select: none;
`

const OverviewActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`


export default TextColumnInfoMobile;
